<template>
  <div class="whitelist-rule-config">
    <!-- 船舶类型匹配配置 -->
    <div v-if="matchType === 'VESSEL_TYPE'" class="config-section">
      <h4 class="config-title">
        <i class="el-icon-ship mr-2"></i>
        船舶类型白名单配置
      </h4>
      <p class="config-description">
        选择的船舶类型将被加入白名单，这些类型的船舶在通过桥梁时不会触发相关告警。
      </p>
      
      <el-form-item label="选择船舶类型" prop="vesselTypes">
        <el-select 
          v-model="config.vesselTypes" 
          multiple
          placeholder="请选择船舶类型"
          style="width: 100%"
          @change="handleConfigChange">
          <el-option
            v-for="type in vesselTypeOptions"
            :key="type.typeCode"
            :label="`${type.typeName} (${type.typeCode})`"
            :value="type.typeCode">
            <span style="float: left">{{ type.typeName }}</span>
            <span style="float: right; color: #8492a6; font-size: 13px">{{ type.typeCode }}</span>
          </el-option>
        </el-select>
      </el-form-item>
      
      <div v-if="config.vesselTypes && config.vesselTypes.length > 0" class="selected-types">
        <h5>已选择的船舶类型：</h5>
        <el-tag
          v-for="typeCode in config.vesselTypes"
          :key="typeCode"
          type="success"
          size="small"
          class="type-tag">
          {{ getVesselTypeName(typeCode) }}
        </el-tag>
      </div>
    </div>
    
    <!-- MMSI数字匹配配置 -->
    <div v-if="matchType === 'MMSI_PATTERN'" class="config-section">
      <h4 class="config-title">
        <i class="el-icon-key mr-2"></i>
        MMSI数字匹配白名单配置
      </h4>
      <p class="config-description">
        设置MMSI匹配模式，符合模式的船舶将被加入白名单。支持通配符匹配。
      </p>
      
      <el-form-item label="MMSI匹配模式" prop="mmsiPattern">
        <el-input 
          v-model="config.mmsiPattern" 
          placeholder="例如：412****** （前3位是412）、**89***** （第3-4位是89）"
          @input="handleConfigChange"
          maxlength="9">
          <template slot="prepend">模式</template>
        </el-input>
      </el-form-item>
      
      <div class="pattern-examples">
        <h5>匹配模式示例（MMSI总长度9位）：</h5>
        <ul>
          <li><code>412******</code> - 匹配前3位是412的MMSI（如：412001234）</li>
          <li><code>**89*****</code> - 匹配第3-4位是89的MMSI（如：128900001）</li>
          <li><code>*****1234</code> - 匹配后4位是1234的MMSI（如：412001234）</li>
          <li><code>412345678</code> - 精确匹配指定MMSI</li>
        </ul>
      </div>
      
      <div v-if="config.mmsiPattern" class="pattern-preview">
        <h5>匹配预览：</h5>
        <p>模式 <code>{{ config.mmsiPattern }}</code> 将匹配：{{ getPatternDescription(config.mmsiPattern) }}</p>
      </div>
    </div>
    
    <!-- 船舶名称匹配配置 -->
    <div v-if="matchType === 'NAME_PATTERN'" class="config-section">
      <h4 class="config-title">
        <i class="el-icon-document mr-2"></i>
        船舶名称模式匹配白名单配置
      </h4>
      <p class="config-description">
        输入船舶名称模式列表，支持通配符*匹配。支持中英文名称匹配。
      </p>
      
      <el-form-item label="名称模式列表" prop="vesselNames">
        <el-input 
          type="textarea" 
          v-model="vesselNamesText" 
          rows="6"
          placeholder="请输入需要匹配的名称，每行一个（支持*通配符）"
          @input="handleVesselNamesChange">
        </el-input>
      </el-form-item>
      
      <div class="pattern-examples">
        <h5>名称模式示例：</h5>
        <ul>
          <li><code>*海警*</code> - 匹配包含"海警"的船舶名称</li>
          <li><code>*COAST GUARD*</code> - 匹配包含"COAST GUARD"的船舶名称</li>
          <li><code>海警*</code> - 匹配以"海警"开头的船舶名称</li>
          <li><code>*01</code> - 匹配以"01"结尾的船舶名称</li>
          <li><code>海警1001</code> - 精确匹配"海警1001"</li>
        </ul>
      </div>
      
      <div v-if="config.vesselNames && config.vesselNames.length > 0" class="vessel-names-preview">
        <h5>已配置的名称模式（共{{ config.vesselNames.length }}个）：</h5>
        <div class="names-grid">
          <el-tag
            v-for="(pattern, index) in config.vesselNames"
            :key="index"
            type="success"
            size="small"
            class="name-tag">
            {{ pattern }}
          </el-tag>
        </div>
      </div>
    </div>
    
    <!-- 规则说明 -->
    <div class="rule-explanation">
      <el-alert
        title="白名单规则说明"
        type="info"
        :closable="false"
        show-icon>
        <template slot="default">
          <p>白名单规则用于定义哪些船舶应该被排除在告警范围之外：</p>
          <ul>
            <li><strong>船舶类型匹配：</strong>指定类型的所有船舶都将被视为白名单船舶</li>
            <li><strong>MMSI数字匹配：</strong>根据MMSI号码模式匹配白名单船舶</li>
            <li><strong>船舶名称匹配：</strong>根据船舶名称（支持模糊匹配）识别白名单船舶</li>
          </ul>
          <p><strong>注意：</strong>白名单船舶不会触发相关告警，但仍会在系统中正常显示和记录。</p>
        </template>
      </el-alert>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WhitelistRuleConfig',
  
  props: {
    matchType: {
      type: String,
      required: true
    },
    config: {
      type: Object,
      default: () => ({})
    },
    vesselTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      vesselNamesText: ''
    };
  },
  
  watch: {
    'config.vesselNames': {
      handler(newVal) {
        if (newVal && Array.isArray(newVal)) {
          this.vesselNamesText = newVal.join('\n');
        }
      },
      immediate: true
    }
  },
  
  methods: {
    // 处理配置变化
    handleConfigChange() {
      this.$emit('config-change', this.config);
    },
    
    // 处理船舶名称变化
    handleVesselNamesChange() {
      if (this.vesselNamesText) {
        this.config.vesselNames = this.vesselNamesText
          .split('\n')
          .map(name => name.trim())
          .filter(name => name !== '');
      } else {
        this.config.vesselNames = [];
      }
      this.handleConfigChange();
    },
    
    // 获取船舶类型名称
    getVesselTypeName(typeCode) {
      const type = this.vesselTypeOptions.find(t => t.typeCode === typeCode);
      return type ? type.typeName : typeCode;
    },
    
    // 获取模式描述
    getPatternDescription(pattern) {
      if (!pattern) return '';
      
      // 如果没有通配符，则是精确匹配
      if (!pattern.includes('*')) {
        return `精确匹配MMSI "${pattern}"`;
      }
      
      // 分析模式结构
      const positions = [];
      for (let i = 0; i < pattern.length; i++) {
        if (pattern[i] !== '*') {
          positions.push(`第${i + 1}位是${pattern[i]}`);
        }
      }
      
      if (positions.length === 0) {
        return '匹配所有MMSI';
      } else if (positions.length === 1) {
        return positions[0] + '的所有MMSI';
      } else {
        return positions.join('，') + '的所有MMSI';
      }
    }
  }
};
</script>

<style scoped>
.whitelist-rule-config {
  padding: 20px;
}

.config-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background-color: #fafafa;
}

.config-title {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c5aa0;
  display: flex;
  align-items: center;
}

.config-description {
  margin: 0 0 20px 0;
  color: #606266;
  line-height: 1.6;
}

.selected-types {
  margin-top: 15px;
}

.selected-types h5 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #606266;
}

.type-tag {
  margin: 0 8px 8px 0;
}

.pattern-examples {
  margin-top: 15px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.pattern-examples h5 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #606266;
}

.pattern-examples ul {
  margin: 0;
  padding-left: 20px;
}

.pattern-examples li {
  margin-bottom: 5px;
  color: #606266;
}

.pattern-examples code {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.pattern-preview {
  margin-top: 15px;
  padding: 10px;
  background-color: #e6f7ff;
  border-radius: 4px;
}

.pattern-preview h5 {
  margin: 0 0 5px 0;
  font-size: 14px;
  color: #1890ff;
}

.pattern-preview code {
  background-color: #fff;
  color: #1890ff;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.vessel-names-preview {
  margin-top: 15px;
}

.vessel-names-preview h5 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #606266;
}

.names-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.name-tag {
  margin: 0;
}

.rule-explanation {
  margin-top: 30px;
}

.rule-explanation ul {
  margin: 10px 0;
  padding-left: 20px;
}

.rule-explanation li {
  margin-bottom: 8px;
  line-height: 1.5;
}

.mr-2 {
  margin-right: 8px;
}
</style>
